import useScreenSize from 'hooks/useScreenSize';
import LocalizedDate, { localizeDate } from 'components/LocalizedDate';
import styles from './styles/Games.module.scss';
import ExternalLink from 'icons/ExternalLink';
import MissingInfoModule from '@/components/modules/MissingInfoModule';
import { COMPLETED } from 'constants/GameStatuses';
import setUtmReferrer from 'helpers/setUtmReferrer';
import PageLayout from '@/components/PageLayout';
import ContentSection, { ContentSectionFlushContent } from '@/components/ContentSection';
import PageHeader from '@/components/PageHeader';
import ApplicationShareButton from 'containers/ApplicationContainer/components/ApplicationShareButton';
import ShareModalDefaultExports from 'components/ShareModalDefaultExports';
import { TeamsGamesQuery, TeamsGamesQueryVariables, rubyQueries } from 'queries';
import Page from '@/components/Page';
import Table, { TableCell, TableHeaderCell, TableLinkRow } from '@/components/Table';
import TeamImage from 'components/TeamImage';
import TextLink, { TextColorVariant, TextLinkSize } from '@/components/TextLink';
import { entryPoint } from 'react_rails/entryPoint';
import TeamSubNav from '@/components/subNavs/TeamSubNav';
import InfinitePagination from '@/components/InfinitePagination';
import { useState } from 'react';
import { CURRENT_SCHOOL_YEAR } from 'constants/schoolYears';
import Button, { ButtonVariant, ButtonWidth } from '@/components/Button';
import Filter from '@/components/Filter';
import CalloutModule from '@/components/modules/CalloutModule';
import GoFanButton from '@/components/affiliates/GoFanButton';
import GameResultText from '@/components/GameResultText';
import { useApplicationContainer } from 'containers/ApplicationContainer';
import MinuteMediaAd from '@/components/ads/MinuteMediaAd';
import StnVideoAd from '@/components/ads/StnVideoAd';
import FilterSection from '@/components/FilterSection';
import OutbrainAd from '@/components/ads/OutbrainAd';
import TeamStructuredData from '@/components/structuredData/TeamStructuredData';

export default entryPoint(function Games({ query: { team }, variables }: { query: TeamsGamesQuery; variables: TeamsGamesQueryVariables }) {
  const { isSmallScreen } = useScreenSize();
  const [, { updateCurrentQueryStringParams }] = useApplicationContainer();
  const [games, setGames] = useState(team.games.nodes);

  if (!team.nextGames || !team.schoolYears) throw 'Missing info'; // for ts

  const nextGame = team.nextGames.nodes[0];

  function handleExport() {
    return {
      title: `${team.name}`,
      subTitle: `${variables.schoolYear || CURRENT_SCHOOL_YEAR} ${team.genderSport.name} ${team.level.name} Season Schedule`,
      tables: [
        {
          header: ['Date', 'Time', 'At', 'Opponent', 'Location', 'Result'],
          body: games.map(game => [
            localizeDate(game.date, 'MMM. DD'),
            localizeDate(game.date, 'h:mm A') || '',
            game.featured.locationDescriptor,
            game.opponent.team.name,
            game.locationText,
            game.featured.scoreText && game.opponent.scoreText
              ? `${game.featured.scoreText}-${game.opponent.scoreText}`
              : game.shortStatusText
          ])
        }
      ]
    };
  }

  return (
    <Page subNav={<TeamSubNav team={team} />}>
      <PageLayout>
        <TeamStructuredData team={team} games={games} />
        {nextGame && (
          <>
            <CalloutModule
              className={styles.NextGame}
              title="Next Game"
              topText={<LocalizedDate date={nextGame.date} format="ddd, MMM DD" />}
              entity={nextGame.opponent.team}
              bottomText={
                <>
                  <span>{nextGame.featured.locationDescriptor}</span> {nextGame.opponent.team.name} {nextGame.opponent.team.mascot}
                </>
              }
              footer={
                <>
                  <Button href={nextGame.webPath} variant={ButtonVariant.WHITE} width={ButtonWidth.FULL}>
                    Game Preview
                  </Button>
                  {nextGame.contest?.gofanTicketingUrl && <GoFanButton href={nextGame.contest.gofanTicketingUrl} />}
                </>
              }
            />
          </>
        )}
        {!isSmallScreen && (
          <div style={{ width: '100%' }}>
            <StnVideoAd placement="INLINE" />
          </div>
        )}
        <ContentSection>
          <PageHeader
            right={
              <ApplicationShareButton>
                <ShareModalDefaultExports data={handleExport} />
              </ApplicationShareButton>
            }
          >
            {team.name} {team.level.name} {team.genderSport.name} Schedule
          </PageHeader>
          <FilterSection>
            <Filter
              pathExtractor={season => updateCurrentQueryStringParams({ season })}
              value={variables.schoolYear || CURRENT_SCHOOL_YEAR}
              options={team.schoolYears}
            />
          </FilterSection>
          {team.games.nodes.length === 0 && <MissingInfoModule />}
          {team.games.nodes.length > 0 && (
            <InfinitePagination
              onLoadMore={({
                team: {
                  games: { nodes, pageInfo }
                }
              }) => {
                setGames(games => [...games, ...nodes]);
                return pageInfo;
              }}
              pageInfo={team.games.pageInfo}
              query={rubyQueries.teamsGamesQuery}
              ttl={60}
              variables={{ ...variables, paginating: true }}
            >
              <ContentSectionFlushContent>
                <Table
                  className={styles.Table}
                  header={
                    <>
                      <TableHeaderCell>Date</TableHeaderCell>
                      <TableHeaderCell>Opponent</TableHeaderCell>
                      <TableHeaderCell className={styles.Result}>Result</TableHeaderCell>
                      <TableHeaderCell>W-L (League)</TableHeaderCell>
                      <TableHeaderCell>Type</TableHeaderCell>
                      <TableHeaderCell>Location</TableHeaderCell>
                      <TableHeaderCell align="right">Tickets</TableHeaderCell>
                      <TableHeaderCell align="right">Live Stream</TableHeaderCell>
                    </>
                  }
                >
                  {team.games.nodes.map(game => (
                    <TableLinkRow key={game.id} href={game.webPath}>
                      <TableCell>
                        <LocalizedDate date={game.date} format={isSmallScreen ? 'MMM DD' : 'ddd, MMM DD'} />
                      </TableCell>
                      <TableCell>
                        <div className={styles.Opponent}>
                          <span className={styles.LocationDescriptor}>{game.featured.locationDescriptor}</span>
                          <TeamImage src={game.opponent.team.image} alt={game.opponent.team.name} className={styles.OpponentImage} />{' '}
                          <TextLink href={game.opponent.team.webPath} size={TextLinkSize.SMALL} color={TextColorVariant.BLACK}>
                            {game.opponent.team.name}
                          </TextLink>
                        </div>
                      </TableCell>
                      <TableCell className={styles.Result}>
                        <TextLink
                          href={game.webPath}
                          size={TextLinkSize.SMALL}
                          className={styles.ResultLink}
                          color={TextColorVariant.BLACK}
                        >
                          <GameResultText game={game}>{game.shortStatusText}</GameResultText>
                        </TextLink>
                      </TableCell>
                      <TableCell>
                        {game.featured.standing.overallRecord} ({game.featured.standing.leagueRecord})
                      </TableCell>
                      <TableCell>{game.gameTypeLabel}</TableCell>
                      <TableCell>{game.locationText}</TableCell>
                      <TableCell align="right">
                        {game.contest?.gofanTicketingUrl && game.statusId !== COMPLETED ? (
                          <TextLink href={setUtmReferrer(game.contest.gofanTicketingUrl, 'team_schedule_gamelink')}>
                            Buy Tickets <ExternalLink color="#4672D6" />
                            {/* TODO external icon in TextLink automatically */}
                          </TextLink>
                        ) : (
                          '-'
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {game.contest?.nfhsNetworkUrl ? (
                          <TextLink href={setUtmReferrer(game.contest.nfhsNetworkUrl, 'team_schedule_gamelink')}>
                            Watch <ExternalLink color="#4672D6" />
                            {/* TODO external icon in TextLink automatically */}
                          </TextLink>
                        ) : (
                          '-'
                        )}
                      </TableCell>
                    </TableLinkRow>
                  ))}
                </Table>
              </ContentSectionFlushContent>
            </InfinitePagination>
          )}
        </ContentSection>
        <div style={{ width: '100%' }}>
          <MinuteMediaAd placement="IN_FEED" />
        </div>
        <OutbrainAd />
      </PageLayout>
    </Page>
  );
});
